import { FunctionComponent, createContext, useContext, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import { WorkflowType } from 'modules/workflows/enums';

export type PublishedJourney = {
  id: number;
  name: string;
  workflowType: WorkflowType;
  enabled: boolean;
};

export type PublishedJourneyResponse = {
  workflows: {
    results: PublishedJourney[];
  };
};

export const fetchPublishedWorkflowsQuery = gql`
fragment workflowInfo on Workflow {
    id
    name
    enabled
    workflowType
}

query getWorkflows($filterInfo: WorkflowFilterInfoInput, $pagination: Pagination, $sort: Sort, $search: String) {
  workflows(
    filterInfo: $filterInfo
    pagination: $pagination
    sort: $sort
    search: $search
  ) {
    info {
        count
        offset
        limit
        page
      }
      results {
        ...workflowInfo
      }
  }
}
`;

export const fetchPublicWorkflowsQueryVariables = (
  searchTerm: string = '',
  workflowIds: number[] | null = null,
) => ({
  filterInfo: {
    workflowType: WorkflowType.PUBLISHED,
    workflowState: ['Enabled', 'Disabled'],
    trigger: null,
    createdByUserId: null,
    updatedByUserId: null,
    labelIds: null,
    workflowIds,
  },
  pagination: {
    limit: 50,
    offset: 0,
  },
  search: searchTerm,
  sort: {
    sortBy: 'Name',
    sortDirection: 'Ascending',
  },
});

type PublishedJourneysContextState = {
  loadPublishedJourneys?: () => void;
  publishedJourneys: PublishedJourney[];
  called: boolean;
  error?: any;
  loading: boolean;
  refetch: any;
};

const PublishedJourneyContext = createContext<PublishedJourneysContextState>({
  loadPublishedJourneys: () => {},
  publishedJourneys: [],
  called: false,
  error: null,
  loading: false,
  refetch: undefined,
});

export const PublishedJourneyContextProvider: FunctionComponent<
  React.PropsWithChildren<unknown>
> = ({ children }) => {
  const [loadPublishedJourneys, { data, called, error, loading, refetch }] = useLazyQuery(
    fetchPublishedWorkflowsQuery,
    {
      fetchPolicy: 'no-cache',
      variables: fetchPublicWorkflowsQueryVariables,
    },
  );

  useEffect(() => {
    loadPublishedJourneys();
  }, []);

  const context: PublishedJourneysContextState = {
    loadPublishedJourneys,
    publishedJourneys:
      ((data as any)?.workflows?.results as PublishedJourney[]) || ([] as PublishedJourney[]),
    called,
    error,
    loading,
    refetch,
  };
  return (
    <PublishedJourneyContext.Provider value={context}>{children}</PublishedJourneyContext.Provider>
  );
};

export const usePublishedJourneyContext = () => useContext(PublishedJourneyContext);
